import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Users, UserCheck, DollarSign, CalendarPlus, Cake, AlertTriangle, Clock, ChevronRight, Gift, TrendingUp, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { currentBranch } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bq = currentBranch ? `?branchId=${currentBranch.id}` : "";
        const [s, r] = await Promise.all([api.get(`/dashboard/stats${bq}`), api.get("/dashboard/reminders")]);
        setStats(s.data);
        setReminders(r.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [currentBranch]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Loading</p>
      </div>
    </div>
  );

  const localDate = new Date();
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;

  const statCards = [
    { label: "TOTAL MEMBERS", value: stats?.totalMembers || 0, icon: Users, accent: "from-blue-500/20 to-blue-500/5", path: "/members" },
    { label: "MALE", value: stats?.maleCount || 0, icon: Users, accent: "from-cyan-500/20 to-cyan-500/5", path: "/members?gender=Male" },
    { label: "FEMALE", value: stats?.femaleCount || 0, icon: Users, accent: "from-pink-500/20 to-pink-500/5", path: "/members?gender=Female" },
    { label: "TODAY SIGNUP", value: stats?.todaySignup || 0, icon: CalendarPlus, accent: "from-emerald-500/20 to-emerald-500/5", path: `/members?joiningFrom=${todayStr}&joiningTo=${todayStr}` },
    { label: "REVENUE MTD", value: `₹${(stats?.revenueMonth || 0).toLocaleString()}`, icon: TrendingUp, accent: "from-amber-500/20 to-amber-500/5" },
    { label: "REVENUE TODAY", value: `₹${(stats?.revenueToday || 0).toLocaleString()}`, icon: DollarSign, accent: "from-green-500/20 to-green-500/5" },
  ];

  const modeData = Object.entries(stats?.modeBreakdown || {}).map(([name, d]) => ({ name, amount: d.amount, count: d.count }));
  const planData = Object.entries(stats?.planCounts || {}).map(([name, count]) => ({ name, count }));

  return (
    <div className="space-y-8 pb-2" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Dashboard</h1>
          <p className="text-xs text-muted-foreground tracking-tight mt-1">Welcome back. Here's your gym at a glance.</p>
        </div>
      </div>

      {/* Stat Cards - Bento Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
        {statCards.map((s, i) => (
          <Card
            key={i}
            className={`group relative overflow-hidden bg-card border-border hover:border-primary/40 transition-all duration-300 ${s.path ? "cursor-pointer" : ""}`}
            data-testid={`stat-card-${i}`}
            onClick={() => s.path && navigate(s.path)}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            <CardContent className="relative p-4 lg:p-5">
              <div className="flex items-center justify-between mb-3">
                <s.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <p className="text-2xl lg:text-3xl font-black font-heading tracking-tight">{s.value}</p>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Payment Mode */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              Revenue by Payment Mode
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {modeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={modeData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `₹${v / 1000}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                    formatter={(v) => [`₹${v.toLocaleString()}`, 'Revenue']}
                  />
                  <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                No payment data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Members by Plan */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Members by Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {planData.length > 0 ? (
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={planData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="count"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {planData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                No plan data available
              </div>
            )}
            {planData.length > 0 && (
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {planData.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-xs text-muted-foreground">{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reminders Section */}
      <div className="mb-5">
        <h2 className="text-lg font-heading uppercase tracking-wide mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Quick Reminders
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Today's Birthdays */}
          <Card className="bg-card border-border group hover:border-pink-500/30 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                <Cake className="h-4 w-4 text-pink-500" />
                Today's Birthdays
                {reminders?.birthdays?.length > 0 && (
                  <Badge className="ml-auto bg-pink-500/15 text-pink-500 border-0 text-[10px] font-mono">
                    {reminders.birthdays.length}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[160px]">
                {reminders?.birthdays?.length > 0 ? reminders.birthdays.map((b, i) => (
                  <Link
                    to={`/members/${b.id}`}
                    key={i}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-secondary/60 transition-colors group/item"
                    data-testid={`birthday-${b.id}`}
                  >
                    <ClickableAvatar
                      src={b.photo}
                      name={b.name}
                      className="h-8 w-8 border border-pink-500/20"
                      fallbackClassName="bg-pink-500/10 text-pink-500 text-xs font-heading"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{b.name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{b.phone}</p>
                    </div>
                    <Gift className="h-3.5 w-3.5 text-pink-500 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </Link>
                )) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-6">
                    <Cake className="h-8 w-8 text-muted-foreground/20 mb-2" />
                    <p className="text-xs text-muted-foreground">No birthdays today</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Expiring Today */}
          <Card className="bg-card border-border group hover:border-red-500/30 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                Expiring Today
                {reminders?.todayExpiryCount > 0 && (
                  <Badge className="ml-auto bg-red-500/15 text-red-500 border-0 text-[10px] font-mono">
                    {reminders.todayExpiryCount}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[160px]">
                {reminders?.todayExpiry?.length > 0 ? reminders.todayExpiry.map((e, i) => (
                  <Link
                    to={`/members/${e.id}`}
                    key={i}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-secondary/60 transition-colors group/item"
                    data-testid={`expiry-today-${e.id}`}
                  >
                    <ClickableAvatar
                      src={e.photo}
                      name={e.name}
                      className="h-8 w-8 border border-red-500/20"
                      fallbackClassName="bg-red-500/10 text-red-500 text-xs font-heading"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{e.name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{e.phone}</p>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover/item:opacity-100 transition-opacity" />
                  </Link>
                )) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-6">
                    <AlertTriangle className="h-8 w-8 text-muted-foreground/20 mb-2" />
                    <p className="text-xs text-muted-foreground">No expirations today</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Expiring in 7 Days */}
          <Card className="bg-card border-border group hover:border-amber-500/30 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Expiring in 7 Days
                {reminders?.upcomingExpiryCount > 0 && (
                  <Badge className="ml-auto bg-amber-500/15 text-amber-500 border-0 text-[10px] font-mono">
                    {reminders.upcomingExpiryCount}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[160px]">
                {reminders?.upcomingExpiry?.length > 0 ? reminders.upcomingExpiry.map((e, i) => (
                  <Link
                    to={`/members/${e.id}`}
                    key={i}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-secondary/60 transition-colors group/item"
                    data-testid={`expiry-upcoming-${e.id}`}
                  >
                    <ClickableAvatar
                      src={e.photo}
                      name={e.name}
                      className="h-8 w-8 border border-amber-500/20"
                      fallbackClassName="bg-amber-500/10 text-amber-500 text-xs font-heading"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{e.name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{e.phone}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500 font-mono shrink-0">
                      {e.endDate?.slice(5, 10)}
                    </Badge>
                  </Link>
                )) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-6">
                    <Clock className="h-8 w-8 text-muted-foreground/20 mb-2" />
                    <p className="text-xs text-muted-foreground">No upcoming expirations</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
